import { useCallback, useRef } from "react";

import { useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { updateRemoteAssetMutation } from "../api/mutations/updateRemoteAsset";
import {
    type UpdateRemoteAssetMutation,
    type UpdateRemoteAssetMutationVariables,
} from "../api/mutations/updateRemoteAsset.gql-generated";

import { messages } from "./messages.generated";
import { formatApolloError } from "./useErrorMessage";
import { type SetMutationStatus } from "./useMutationStatus";

interface UpdateFields {
    name: string;
    description: string;
    forkingAllowed?: boolean | null;
}

interface UpdateRemoteAssetResult {
    updateAsset: (id: string, fields: UpdateFields) => Promise<boolean>;
}

export const useUpdateRemoteAsset = (
    setStatus: SetMutationStatus,
    options?: {
        onCompleted?: (updated: NonNullable<UpdateRemoteAssetMutation["updateRemoteAsset"]>) => void;
    },
): UpdateRemoteAssetResult => {
    const intl = useIntl();
    const onCompletedRef = useRef(options?.onCompleted);
    onCompletedRef.current = options?.onCompleted;

    const [updateMutation] = useMutation<UpdateRemoteAssetMutation, UpdateRemoteAssetMutationVariables>(
        updateRemoteAssetMutation,
    );

    const updateAsset = useCallback(
        async (id: string, fields: UpdateFields) => {
            setStatus({ type: "pending", action: "update" });
            try {
                const result = await updateMutation({
                    variables: {
                        id,
                        name: fields.name,
                        description: fields.description,
                        forkingAllowed: fields.forkingAllowed ?? null,
                    },
                });
                if (result.data?.updateRemoteAsset) {
                    setStatus({ type: "success", action: "update" });
                    onCompletedRef.current?.(result.data.updateRemoteAsset);
                    return true;
                } else {
                    setStatus({
                        type: "error",
                        action: "update",
                        message: intl.formatMessage(messages.updateAssetError),
                    });
                    return false;
                }
            } catch (error) {
                console.error("Failed to update remote asset:", error);
                setStatus({
                    type: "error",
                    action: "update",
                    message: formatApolloError(error, intl),
                });
                return false;
            }
        },
        [updateMutation, setStatus, intl],
    );

    return { updateAsset };
};
